import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import {
  createCreditReport,
  deleteCreditReport,
  getCreditUtilization,
  listCreditReports,
  type Bureau,
  type CreateCreditReportInput,
  type CreditReport,
  type FreezeStatus,
} from "../lib/api-modules/credit.js";
import {
  Button,
  Card,
  DateField,
  EmptyState,
  NumberField,
  Pill,
  ProgressBar,
  SectionHeader,
  SelectField,
  StatCard,
  TextareaField,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /credit — Phase 8 Session 2 credit health.
 *
 * v1 = manual entry per (user, bureau, date). 5 bureaus available
 * (FICO + Equifax + Experian + TransUnion + VantageScore). Latest score
 * per bureau headlines as a tile + utilization computed from
 * `accounts.account_type === "credit_card"` (limit vs balance).
 */

const BUREAUS: Bureau[] = [
  "fico",
  "equifax",
  "experian",
  "transunion",
  "vantage_score",
];
const BUREAU_LABEL: Record<Bureau, string> = {
  fico: "FICO",
  equifax: "Equifax",
  experian: "Experian",
  transunion: "TransUnion",
  vantage_score: "VantageScore",
};
const FREEZE_OPTIONS: FreezeStatus[] = ["frozen", "thawed", "fraud_alert"];

export function CreditPage() {
  const { user } = useAuth();
  const toast = useToast();
  const reportsQ = useQuery({
    queryKey: ["credit-reports"],
    queryFn: listCreditReports,
  });
  const utilQ = useQuery({
    queryKey: ["credit-utilization"],
    queryFn: getCreditUtilization,
  });
  const reports = reportsQ.data?.credit_reports ?? [];

  const latestByBureau = new Map<Bureau, CreditReport>();
  for (const r of reports) {
    const cur = latestByBureau.get(r.bureau);
    if (!cur || r.score_date > cur.score_date) {
      latestByBureau.set(r.bureau, r);
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="Credit health"
        sub="Manual score history per bureau + computed utilization across credit-card accounts. v1 — bureau auto-pulls land in Phase 11."
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {BUREAUS.map((b) => {
          const r = latestByBureau.get(b);
          return (
            <StatCard
              key={b}
              label={BUREAU_LABEL[b]}
              value={r ? r.score : "—"}
              sub={r ? r.score_date : "No score yet"}
            />
          );
        })}
      </div>

      {utilQ.data && (
        <Card className="mb-6">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
            Utilization across credit cards
          </p>
          <p className="font-mono text-2xl tabular-nums">
            {utilQ.data.utilization.pct.toFixed(1)}%
          </p>
          <p className="text-xs text-[var(--color-muted)]">
            ${(utilQ.data.utilization.balance_cents / 100).toLocaleString()} balance ·
            ${(utilQ.data.utilization.limit_cents / 100).toLocaleString()} limit ·{" "}
            {utilQ.data.utilization.account_count} card
            {utilQ.data.utilization.account_count === 1 ? "" : "s"}
          </p>
          <ProgressBar
            value={utilQ.data.utilization.pct}
            max={100}
            tone={
              utilQ.data.utilization.pct < 30
                ? "success"
                : utilQ.data.utilization.pct < 50
                  ? "warning"
                  : "danger"
            }
            className="mt-3"
            ariaLabel="Credit utilization"
          />
          <p className="mt-3 text-xs text-[var(--color-muted)]">
            Aim for &lt;30% — that's the threshold most credit-scoring models
            stop dinging you.
          </p>
        </Card>
      )}

      {user && <AddScoreForm userId={user.user_id} onCreated={() => toast.show("Score added")} />}

      <SectionHeader
        title="History"
        className="mt-8"
      />
      {reportsQ.isLoading && <p className="text-sm">Loading...</p>}
      {!reportsQ.isLoading && reports.length === 0 ? (
        <EmptyState
          title="No scores recorded yet"
          description="Add your first FICO + the per-bureau pulls from your annualcreditreport.com fetch."
        />
      ) : (
        <Card padded={false}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-default)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Bureau</th>
                <th className="px-4 py-2">Score</th>
                <th className="px-4 py-2">Freeze</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <CreditReportRow
                  key={`${r.user_id}#${r.score_date}#${r.bureau}`}
                  report={r}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Toast message={toast.message} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.credit} />
      </div>
    </div>
  );
}

function CreditReportRow({ report }: { report: CreditReport }) {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => deleteCreditReport(report.user_id, report.score_date),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["credit-reports"] });
      void queryClient.invalidateQueries({ queryKey: ["credit-utilization"] });
    },
  });
  return (
    <tr className="border-b border-[var(--color-border-default)] last:border-b-0">
      <td className="px-4 py-2 font-mono text-xs tabular-nums">{report.score_date}</td>
      <td className="px-4 py-2 font-mono text-xs">{report.user_id.slice(0, 10)}…</td>
      <td className="px-4 py-2">{BUREAU_LABEL[report.bureau] ?? report.bureau}</td>
      <td className="px-4 py-2 font-mono tabular-nums">{report.score}</td>
      <td className="px-4 py-2">
        {report.freeze_status ? <Pill tone="accent">{report.freeze_status}</Pill> : "—"}
      </td>
      <td className="px-4 py-2 text-right">
        <ConfirmButton
          label="Delete"
          confirmLabel="Confirm delete"
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
        />
      </td>
    </tr>
  );
}

function AddScoreForm({
  userId,
  onCreated,
}: {
  userId: string;
  onCreated: () => void;
}) {
  const queryClient = useQueryClient();
  const [bureau, setBureau] = useState<Bureau>("fico");
  const [score, setScore] = useState("");
  const [scoreDate, setScoreDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [freeze, setFreeze] = useState<FreezeStatus | "">("");
  const [notes, setNotes] = useState("");

  const createMutation = useMutation({
    mutationFn: (input: CreateCreditReportInput) => createCreditReport(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["credit-reports"] });
      void queryClient.invalidateQueries({ queryKey: ["credit-utilization"] });
      onCreated();
      setScore("");
      setNotes("");
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = Number(score);
    if (!Number.isInteger(parsed) || parsed < 300 || parsed > 850) return;
    const input: CreateCreditReportInput = {
      user_id: userId,
      bureau,
      score: parsed,
      score_date: scoreDate,
    };
    if (freeze !== "") input.freeze_status = freeze;
    if (notes.trim() !== "") input.notes = notes.trim();
    createMutation.mutate(input);
  }

  return (
    <Card>
      <p className="mb-3 text-sm font-medium">Add a score</p>
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-5">
        <SelectField
          label="Bureau"
          value={bureau}
          onChange={(e) => setBureau(e.target.value as Bureau)}
        >
          {BUREAUS.map((b) => (
            <option key={b} value={b}>
              {BUREAU_LABEL[b]}
            </option>
          ))}
        </SelectField>
        <NumberField
          label="Score"
          value={score}
          onChange={(e) => setScore(e.target.value)}
          min={300}
          max={850}
          required
        />
        <DateField
          label="Score date"
          value={scoreDate}
          onChange={(e) => setScoreDate(e.target.value)}
          required
        />
        <SelectField
          label="Freeze (optional)"
          value={freeze}
          onChange={(e) => setFreeze(e.target.value as FreezeStatus | "")}
        >
          <option value="">—</option>
          {FREEZE_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </SelectField>
        <div className="flex items-end">
          <Button
            type="submit"
            disabled={createMutation.isPending || score === ""}
          >
            {createMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
        <TextareaField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="sm:col-span-5"
        />
      </form>
    </Card>
  );
}
